/**
 * Component: MerchantApprovalTable
 * Table component for merchant approval list
 */

import { useState, useRef, useCallback } from "react";
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
  TableLoadingRow,
  TableEmptyRow,
  Pagination,
} from "@/components/ui/table/table";
import { MoreIcon } from "@/assets/svg";
import { Button } from "@/components/ui/button";
import { StatusBadge } from "@/components/ui/status-badge";
import { formatDate } from "@/utils/Format";
import type { MerchantApprovalItem, MerchantApprovalPaging } from "../../../domain/model/MerchantApprovalModel";

const getApprovalStatusBadgeVariant = (
  status?: string,
): "success" | "error" | "warning" | "info" | "default" => {
  switch (status?.toUpperCase()) {
    case "APPROVED":
      return "success";
    case "REJECTED":
      return "error";
    case "PENDING":
      return "warning";
    default:
      return "default";
  }
};

const getApprovalStatusLabel = (status?: string): string => {
  switch (status?.toUpperCase()) {
    case "APPROVED":
      return "Approved";
    case "REJECTED":
      return "Rejected";
    case "PENDING":
      return "Pending";
    default:
      return status || "Unknown";
  }
};

export interface MerchantApprovalTableProps {
  data: MerchantApprovalItem[];
  paging: MerchantApprovalPaging;
  isLoading?: boolean;
  onPageChange: (page: number) => void;
  onView?: (item: MerchantApprovalItem) => void;
}

export const MerchantApprovalTable = ({
  data,
  paging,
  isLoading = false,
  onPageChange,
  onView,
}: MerchantApprovalTableProps): React.JSX.Element => {
  const [openRowId, setOpenRowId] = useState<string | null>(null);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, right: 0 });
  const dropdownRef = useRef<HTMLDivElement>(null);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const cancelClose = useCallback(() => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  }, []);

  const scheduleClose = useCallback(() => {
    cancelClose();
    closeTimer.current = setTimeout(() => setOpenRowId(null), 200);
  }, [cancelClose]);

  const handleMouseEnter = (
    e: React.MouseEvent<HTMLButtonElement>,
    id?: string,
  ) => {
    if (!id) return;

    cancelClose();
    const rect = e.currentTarget.getBoundingClientRect();
    setDropdownPos({
      top: rect.bottom + window.scrollY + 4,
      right: window.innerWidth - rect.right,
    });
    setOpenRowId(id);
  };

  return (
    <div className="flex flex-col gap-4">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Merchant ID</TableHead>
            <TableHead>Merchant Name</TableHead>
            <TableHead>Phone Number</TableHead>
            <TableHead>Email</TableHead>
            <TableHead>Registration Date</TableHead>
            <TableHead>Jenis Usaha</TableHead>
            <TableHead>Approval Status</TableHead>
            <TableHead> </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {isLoading ? (
            <TableLoadingRow
              colSpan={8}
              message="Loading merchant approvals..."
            />
          ) : data.length === 0 ? (
            <TableEmptyRow colSpan={8} message="No merchant approvals found." />
          ) : (
            data.map((item) => (
              <TableRow key={item.merchantId}>
                <TableCell className="font-mono text-sm">
                  {item.merchantId}
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-2">
                    {item.brandName && (
                      <div className="w-8 h-8 rounded-lg overflow-hidden shrink-0">
                        <div className="w-full h-full bg-slate-100 text-xs text-slate-600 flex items-center justify-center font-medium">
                          {item.brandName.slice(0, 1).toUpperCase()}
                        </div>
                      </div>
                    )}
                    <span>{item.merchantName}</span>
                  </div>
                </TableCell>
                <TableCell>{item.phoneNumber}</TableCell>
                <TableCell>{item.email}</TableCell>
                <TableCell>{formatDate(item.registrationDate)}</TableCell>
                <TableCell>{item.merchantType}</TableCell>
                <TableCell>
                  <StatusBadge
                    variant={getApprovalStatusBadgeVariant(item.approvalStatus)}
                  >
                    {getApprovalStatusLabel(item.approvalStatus)}
                  </StatusBadge>
                </TableCell>
                <TableCell className="text-right">
                  <Button
                    variant="ghost"
                    size="sm"
                    icon={<MoreIcon />}
                    aria-label={`More actions for ${item.merchantName}`}
                    onMouseEnter={(e) => handleMouseEnter(e, item.merchantId)}
                    onMouseLeave={scheduleClose}
                  />
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>

      {openRowId && (
        <div
          ref={dropdownRef}
          style={{
            position: "fixed",
            top: dropdownPos.top,
            right: dropdownPos.right,
          }}
          className="w-40 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50"
          onMouseEnter={cancelClose}
          onMouseLeave={scheduleClose}
        >
          {onView && (
            <button
              onClick={() => {
                const item = data.find((d) => d.merchantId === openRowId);
                if (item) onView(item);
                setOpenRowId(null);
              }}
              className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 transition-colors"
            >
              View Details
            </button>
          )}
        </div>
      )}

      <Pagination
        currentPage={paging.currentPage}
        totalPages={paging.totalPages}
        onPageChange={onPageChange}
      />
    </div>
  );
};

